/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useCallback, useEffect } from 'react';
import maasAccount from './hooks/useMaasAccounts';
import { IMaas, IMaasAccount } from './models';
import Alert from './Alert';
import { getTenantIdFromUrl } from './helpers/router';
import PageError from './PageError';

const MaasContext = React.createContext<IMaas>({});
const useMaasAccount = () => React.useContext(MaasContext) as IMaas | Record<string, never>;

const MaasAccount: React.FC<IMaasAccount> = ({
    children,
    config,
    LoadingComponent,
    PageError: CustomPageError,
}: IMaasAccount) => {
    if (!config.keycloakToken) throw Error('keycloakToken is required');

    const {
        user,
        sellers,
        sellerPermissions,
        currentServices,
        currentSeller,
        setCurrentSeller,
        onSellerChanged,
        loading,
        error,
        setError,
        responseError,
    } = maasAccount({
        config,
    });

    const verifySellerIsDifferent = useCallback(() => {
        // verifica se o seller esta no localstorage e utiliza ele se existir
        const tenantIdSeller =
            sessionStorage.getItem('maas-tenantId') ||
            sellers.find((seller) => seller.current)?.tenantId ||
            sellers?.[0]?.tenantId;

        // verifica se existe tenantId via config props ou buscar no query params
        const tenantIdByUrl = config.tenantId || getTenantIdFromUrl();

        // se existir via props ou query params da prioridade
        setCurrentSeller(tenantIdByUrl || tenantIdSeller);
    }, [config.tenantId, sellers, setCurrentSeller]);

    useEffect(() => {
        if (!loading) verifySellerIsDifferent();
    }, [loading]);

    if (loading || currentSeller === undefined) return <>{LoadingComponent}</>;

    if (!currentSeller) {
        return CustomPageError || <PageError />;
    }

    const initialValues: IMaas = {
        user,
        currentSeller,
        currentServices,
        sellerPermissions,
        changeSeller: setCurrentSeller,
        onSellerChanged,
        sellers,
        error,
        responseError,
    };

    return (
        <MaasContext.Provider value={initialValues}>
            <MaasContext.Consumer>
                {(values) => {
                    if (values?.error?.error) {
                        return (
                            <Alert message={error.message} onDismiss={() => setError({ error: false, message: '' })} />
                        );
                    }
                }}
            </MaasContext.Consumer>
            {children}
        </MaasContext.Provider>
    );
};

export { useMaasAccount };
export type { IMaasAccount, IMaas };
export default MaasAccount;
